import { apiClient } from './client';

/**
 * Uploads are a three-step flow: presign a *staging* key, POST the bytes
 * straight to S3 under a policy S3 itself enforces, then confirm.
 *
 * How the confirm step works depends on the intent, and getting it wrong fails
 * silently — see `uploadAndConfirm` and `uploadToStaging` below.
 */
export type UploadIntent =
    | 'logo'
    | 'letterhead'
    | 'anamnesis'
    | 'publisher_results'
    | 'report_import'
    | 'report_image';

/**
 * The only intents `POST /uploads/confirm` accepts — those with no feature
 * endpoint of their own to hand the staging key to.
 */
export type ConfirmableUploadIntent = Extract<UploadIntent, 'logo' | 'letterhead' | 'report_image'>;

interface PresignPostResponse {
    url: string;
    fields: Record<string, string>;
    key: string;
}

export const DOCX_MIME_TYPE =
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

const MIME_TYPE_BY_EXTENSION: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    pdf: 'application/pdf',
    docx: DOCX_MIME_TYPE,
    txt: 'text/plain',
};

/**
 * The mimetype to presign with. Browsers leave `File.type` empty often enough
 * — notably for `.docx` on some platforms — and presigning requires a real
 * mimetype, so fall back to the one the extension implies. An unknown
 * extension deliberately yields the empty string: the backend answers that
 * with a pt-BR "Tipo de arquivo não suportado".
 */
export const mimetypeForFile = (file: File): string => {
    if (file.type) return file.type;

    const extension = file.name.toLowerCase().split('.').pop() ?? '';
    return MIME_TYPE_BY_EXTENSION[extension] ?? '';
};

/**
 * S3 evaluates a POST policy against the fields that precede the file and
 * ignores everything after it, so `file` must be appended last.
 */
export const buildUploadFormData = (fields: Record<string, string>, file: File): FormData => {
    const form = new FormData();

    for (const [name, value] of Object.entries(fields)) {
        form.append(name, value);
    }
    form.append('file', file);

    return form;
};

const presignPost = async (body: {
    mimetype: string;
    intent: UploadIntent;
}): Promise<PresignPostResponse> => {
    // The apiClient interceptor unwraps to the standard { data: { ... } } wrapper.
    const response = (await apiClient.post('/uploads/presigned-post', body)) as unknown as {
        data: PresignPostResponse;
    };
    return response.data;
};

/**
 * Send the bytes to S3. Two mechanics beyond the field order above: no
 * `Content-Type` header — the browser has to set it so it can pick the
 * multipart boundary, and the signed content type travels as a *field* — and
 * a successful POST answers 204 with an empty body, not 200.
 */
const uploadViaPresignedPost = (
    url: string,
    fields: Record<string, string>,
    file: File,
    onProgress?: (progress: number) => void,
): Promise<void> => {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url);

        if (onProgress) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                    const percentComplete = Math.round((e.loaded / e.total) * 100);
                    onProgress(percentComplete);
                }
            };
        }

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        };

        xhr.onerror = () => reject(new Error('Network Error during upload'));
        xhr.send(buildUploadFormData(fields, file));
    });
};

interface UploadInput {
    file: File;
    intent: UploadIntent;
    onProgress?: (progress: number) => void;
}

export const uploadsApi = {
    /**
     * Presign and upload, returning the **staging** key.
     *
     * For `anamnesis`, `report_import` and `publisher_results` this is the whole
     * client-side flow: the feature's own endpoint takes the staging key and
     * validates the object itself. Never confirm those keys here — /confirm
     * would consume the object and hand back a reference that endpoint rejects.
     */
    uploadToStaging: async ({ file, intent, onProgress }: UploadInput): Promise<string> => {
        const { url, fields, key } = await presignPost({
            mimetype: mimetypeForFile(file),
            intent,
        });

        await uploadViaPresignedPost(url, fields, file, onProgress);

        return key;
    },

    /**
     * Upload and confirm, returning the durable reference — the only value that
     * may be persisted on a report or profile. Store it verbatim; it already
     * carries the `s3-key:` prefix.
     *
     * Only `logo`, `letterhead` and `report_image` are confirmed this way.
     */
    uploadAndConfirm: async ({
        file,
        intent,
        onProgress,
    }: UploadInput & { intent: ConfirmableUploadIntent }): Promise<string> => {
        const key = await uploadsApi.uploadToStaging({ file, intent, onProgress });

        const response = (await apiClient.post('/uploads/confirm', {
            key,
            intent,
        })) as unknown as { data: { key: string } };

        return response.data.key;
    },
};
